import axios from 'axios';
import { io } from 'socket.io-client';
import { getConfig } from '@/config';
import { isLocal, isStaging } from '@/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CheckType =
  | 'fetch'
  | 'socketio'
  | 'image'
  | 'signed-image'
  | 'signed-upload';
export type CheckStatus = 'pending' | 'running' | 'pass' | 'fail';

export interface CheckConfig {
  id: string;
  label: string;
  url: string;
  type: CheckType;
  timeoutMs: number;
  domain: string;
}

export interface CheckResult {
  id: string;
  status: CheckStatus;
  durationMs?: number;
  error?: string;
}

// ---------------------------------------------------------------------------
// Check configuration
// ---------------------------------------------------------------------------

const getCdnHost = () =>
  process.env.NEXT_PUBLIC_APP_ENV === 'dev' ||
  process.env.NEXT_PUBLIC_APP_ENV === 'pr'
    ? 'images-cdn-staging.curipod.com'
    : 'images-cdn.curipod.com';

const getGcsBucket = () =>
  isLocal() || isStaging()
    ? 'images-curipod-staging'
    : 'curipod-images-cdn-bucket';

export function getChecks(): CheckConfig[] {
  const { backend } = getConfig();

  return [
    {
      id: 'curipod-api',
      label: 'Curipod API',
      url: `${backend.coreApiUrl}/v0.5/connectivity/status`,
      type: 'fetch',
      timeoutMs: 8000,
      domain: 'api.curipod.com',
    },
    {
      id: 'game-api-http',
      label: 'Live Classroom (HTTP)',
      url: `${backend.liveApiUrl}/v0.5/connectivity/status`,
      type: 'fetch',
      timeoutMs: 8000,
      domain: 'api-live.curipod.com',
    },
    {
      id: 'game-api-ws',
      label: 'Live Classroom (WebSocket)',
      url: backend.liveApiUrl,
      type: 'socketio',
      timeoutMs: 10000,
      domain: 'api-live.curipod.com',
    },
    {
      id: 'cdn-images',
      label: 'Lesson Images',
      url: `https://${getCdnHost()}/connectivity/connectivity-check.png`,
      type: 'image',
      timeoutMs: 8000,
      domain: getCdnHost(),
    },
    {
      id: 'gcs-storage',
      label: 'Image Uploads',
      url: `https://storage.googleapis.com/${getGcsBucket()}/connectivity/connectivity-check.png`,
      type: 'image',
      timeoutMs: 8000,
      domain: 'storage.googleapis.com',
    },
    {
      id: 'gcs-signed-image',
      label: 'Lesson Images (Signed URLs)',
      url: `${backend.coreApiUrl}/v0.5/files/public/downloadUrl`,
      type: 'signed-image',
      timeoutMs: 10000,
      domain: 'storage.googleapis.com',
    },
    {
      id: 'gcs-signed-upload',
      label: 'Drawing Uploads (Signed URLs)',
      url: `${backend.liveApiUrl}/v0.5/connectivity/uploadUrl`,
      type: 'signed-upload',
      timeoutMs: 10000,
      domain: 'storage.googleapis.com',
    },
  ];
}

// ---------------------------------------------------------------------------
// Individual check runners
// ---------------------------------------------------------------------------

async function runFetchCheck(config: CheckConfig): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), config.timeoutMs);

  try {
    const res = await fetch(config.url, {
      mode: 'cors',
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } finally {
    clearTimeout(timer);
  }
}

function runSocketIOCheck(config: CheckConfig): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      socket.disconnect();
      reject(new Error('Timeout'));
    }, config.timeoutMs);

    const socket = io(`${config.url}/connectivity`, {
      transports: ['websocket'],
    });

    socket.on('connect', () => {
      socket.emit('ping');
    });

    socket.on('pong', () => {
      clearTimeout(timer);
      socket.disconnect();
      resolve();
    });

    socket.on('connect_error', (err) => {
      clearTimeout(timer);
      socket.disconnect();
      reject(err);
    });
  });
}

async function runSignedImageCheck(config: CheckConfig): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), config.timeoutMs);

  try {
    const res = await fetch(config.url, {
      method: 'POST',
      mode: 'cors',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'x-api-tenant': 'connectivity',
      },
      body: JSON.stringify({
        filename: 'connectivity-check.png',
        folder: 'sample',
      }),
    });
    if (!res.ok)
      throw new Error(`Signed URL request failed: HTTP ${res.status}`);
    const data = (await res.json()) as { url?: string };
    if (!data.url) throw new Error('Signed URL response missing url');

    await new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Signed image failed to load'));
      img.src = data.url as string;
    });
  } finally {
    clearTimeout(timer);
  }
}

// 1x1 transparent PNG — the smallest real image, so the PUT looks like a
// drawing upload to a content filter.
const UPLOAD_CHECK_PNG_BASE64 =
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

// Mirrors how a student's drawing is saved (`Files.uploadFile`): an XHR PUT
// with an image Content-Type to a signed URL. The image/signed-image checks
// only do <img> GETs, which filters can let through while blocking this —
// seen at a school where every drawing upload got no response but the
// diagnose page passed.
async function runSignedUploadCheck(config: CheckConfig): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), config.timeoutMs);

  try {
    const res = await fetch(config.url, {
      method: 'POST',
      mode: 'cors',
      signal: controller.signal,
    });
    if (!res.ok)
      throw new Error(`Upload URL request failed: HTTP ${res.status}`);
    const data = (await res.json()) as { url?: string };
    if (!data.url) throw new Error('Upload URL response missing url');

    const bytes = Uint8Array.from(atob(UPLOAD_CHECK_PNG_BASE64), (c) =>
      c.charCodeAt(0),
    );
    try {
      await axios.request({
        url: data.url,
        method: 'PUT',
        headers: { 'Content-Type': 'image/png' },
        data: new Blob([bytes], { type: 'image/png' }),
        timeout: config.timeoutMs,
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        throw new Error(`Upload failed: HTTP ${err.response.status}`);
      }
      throw new Error('Upload failed: no response');
    }
  } finally {
    clearTimeout(timer);
  }
}

function runImageCheck(config: CheckConfig): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Timeout'));
    }, config.timeoutMs);

    const img = new Image();
    img.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error('Image failed to load'));
    };
    img.src = `${config.url}?t=${Date.now()}`;
  });
}

// ---------------------------------------------------------------------------
// Diagnostic runner
// ---------------------------------------------------------------------------

export async function runChecks(
  checks: CheckConfig[],
  onUpdate: (id: string, result: Partial<CheckResult>) => void,
): Promise<CheckResult[]> {
  const promises = checks.map(async (check): Promise<CheckResult> => {
    const start = performance.now();
    onUpdate(check.id, { status: 'running' });

    try {
      switch (check.type) {
        case 'fetch':
          await runFetchCheck(check);
          break;
        case 'socketio':
          await runSocketIOCheck(check);
          break;
        case 'image':
          await runImageCheck(check);
          break;
        case 'signed-image':
          await runSignedImageCheck(check);
          break;
        case 'signed-upload':
          await runSignedUploadCheck(check);
          break;
      }

      const durationMs = Math.round(performance.now() - start);
      const result: CheckResult = {
        id: check.id,
        status: 'pass',
        durationMs,
      };
      onUpdate(check.id, result);
      return result;
    } catch (err) {
      const durationMs = Math.round(performance.now() - start);
      const result: CheckResult = {
        id: check.id,
        status: 'fail',
        durationMs,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
      onUpdate(check.id, result);
      return result;
    }
  });

  const settled = await Promise.allSettled(promises);
  return settled.map((s) =>
    s.status === 'fulfilled'
      ? s.value
      : { id: 'unknown', status: 'fail' as const, error: 'Unexpected error' },
  );
}

// ---------------------------------------------------------------------------
// IT admin message
// ---------------------------------------------------------------------------

export function generateITAdminMessage(
  results: CheckResult[],
  checks: CheckConfig[],
): string {
  const checkMap = new Map(checks.map((c) => [c.id, c]));
  const failed = results.filter((r) => r.status === 'fail');
  const passed = results.filter((r) => r.status === 'pass');

  const lines: string[] = [
    'Curipod Connectivity Diagnostic Report',
    '=======================================',
    `Date: ${new Date().toISOString()}`,
    '',
  ];

  if (failed.length > 0) {
    lines.push('BLOCKED SERVICES:');
    for (const r of failed) {
      const c = checkMap.get(r.id);
      lines.push(`  - ${c?.label ?? r.id} (${c?.domain ?? '?'}) — FAILED`);
    }
    lines.push('');
  }

  if (passed.length > 0) {
    lines.push('PASSED SERVICES:');
    for (const r of passed) {
      const c = checkMap.get(r.id);
      lines.push(`  - ${c?.label ?? r.id} (${c?.domain ?? '?'}) — OK`);
    }
    lines.push('');
  }

  if (failed.length > 0) {
    lines.push('ACTION REQUIRED:');
    lines.push(
      'Please allowlist the following domains in your firewall AND web content filter (e.g. GoGuardian, Securly, Lightspeed):',
    );
    const domains = Array.from(
      new Set(
        failed
          .map((r) => checkMap.get(r.id)?.domain)
          .filter((d): d is string => Boolean(d)),
      ),
    );
    domains.forEach((d) => {
      lines.push(`  - ${d} (HTTPS/WSS, port 443)`);
    });
  }

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Smart hints
// ---------------------------------------------------------------------------

export function getSmartHints(results: CheckResult[]): string[] {
  const resultMap = new Map(results.map((r) => [r.id, r]));
  const hints: string[] = [];

  const httpPass = resultMap.get('game-api-http')?.status === 'pass';
  const wsFail = resultMap.get('game-api-ws')?.status === 'fail';
  if (httpPass && wsFail) {
    hints.push(
      'Your network may be blocking WebSocket connections. Ask your IT admin to allow WSS traffic on port 443 to api-live.curipod.com.',
    );
  }

  const allApiFail =
    resultMap.get('curipod-api')?.status === 'fail' &&
    resultMap.get('game-api-http')?.status === 'fail';
  if (allApiFail) {
    hints.push(
      'All Curipod API services are unreachable. Your network may have a content filter or firewall blocking *.curipod.com domains.',
    );
  }

  const cdnFail = resultMap.get('cdn-images')?.status === 'fail';
  const gcsFail = resultMap.get('gcs-storage')?.status === 'fail';
  if (cdnFail && !allApiFail) {
    hints.push(
      'Image loading from the Curipod CDN is blocked. Ask IT to allowlist images-cdn.curipod.com.',
    );
  }
  if (gcsFail && !cdnFail) {
    hints.push(
      'Google Cloud Storage is blocked. This may indicate a restrictive content filter blocking storage.googleapis.com.',
    );
  }

  const gcsPass = resultMap.get('gcs-storage')?.status === 'pass';
  const signedFail = resultMap.get('gcs-signed-image')?.status === 'fail';
  if (gcsPass && signedFail) {
    hints.push(
      'Your network allows storage.googleapis.com but blocks signed URLs (URLs with long query strings like X-Goog-Signature). Ask your IT admin to allow signed Google Cloud Storage URLs — content filters sometimes strip or block them.',
    );
  }

  const signedImagePass = resultMap.get('gcs-signed-image')?.status === 'pass';
  const uploadFail = resultMap.get('gcs-signed-upload')?.status === 'fail';
  if (signedImagePass && uploadFail) {
    hints.push(
      'Your network can download from Google Cloud Storage but blocks uploads to it, so students will not be able to submit drawings. Ask your IT admin to allow HTTPS PUT requests to storage.googleapis.com/snapmentor-prod.appspot.com/* in the firewall and web content filter.',
    );
  }

  return hints;
}
